/**
 * Scoring + disqualification → outcome resolution.
 *
 * Disqualification does NOT reject inline (PLAN.md §8.5). The user completes
 * the whole funnel; every matched `dq_rule`'s reason accumulates; the result
 * step resolves the outcome:
 *   - any DQ matched              → outcome "disqualified"
 *   - else, score-band tier from total score
 */
import type { Answers, FunnelConfig, Outcome, Tier } from "../types";
import { matchGroup } from "./conditions";

export const DISQUALIFIED = "disqualified";

/** Sum the points of every score rule whose conditions match. */
export function computeScore(config: FunnelConfig, answers: Answers): number {
  let score = 0;
  for (const rule of config.scoring.score_rules ?? []) {
    if (matchGroup(rule.conditions, answers)) score += rule.points;
  }
  return score;
}

/** Reasons from every matched DQ rule, in config order. */
export function collectDqReasons(config: FunnelConfig, answers: Answers): string[] {
  const reasons: string[] = [];
  for (const rule of config.scoring.dq_rules) {
    if (matchGroup(rule.conditions, answers)) reasons.push(rule.reason);
  }
  return reasons;
}

/** First tier whose [score_min, score_max] band contains the score; an
 *  omitted bound is open. Falls back to the first tier if none match. */
export function pickTier(tiers: Tier[], score: number): Tier {
  for (const tier of tiers) {
    const okMin = tier.score_min === undefined || score >= tier.score_min;
    const okMax = tier.score_max === undefined || score <= tier.score_max;
    if (okMin && okMax) return tier;
  }
  // tiers is guaranteed non-empty by the schema (minItems: 1).
  return tiers[0]!;
}

/** Resolve the final outcome after the funnel completes. */
export function resolveOutcome(config: FunnelConfig, answers: Answers): Outcome {
  const score = computeScore(config, answers);

  if (config.scoring.enabled) {
    const dqReasons = collectDqReasons(config, answers);
    if (dqReasons.length > 0) {
      return { tier_id: DISQUALIFIED, qualified: false, score, dq_reasons: dqReasons };
    }
  }

  const tier = pickTier(config.scoring.tiers, score);
  return { tier_id: tier.id, qualified: true, score, dq_reasons: [] };
}
